import { useEffect, useState } from "react";
import { useSync } from "@/lib/live";
import { Link } from "react-router-dom";
import { ChevronRight, Inbox, FileDown } from "lucide-react";
import { api, fmtDate, todayIST, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageTitle, BigButton } from "@/components/Layout";
import { StatusBadge } from "@/components/Status";
import { useNavigate } from "react-router-dom";

const Row = ({ b }) => {
  const { t, lang } = useApp();
  return (
    <Link to={`/history/${b.booking_id}`} data-testid={`history-item-${b.booking_id}`}
      className="flex items-center gap-3 rounded-2xl border border-[#D2E0D8] bg-white p-4 transition-shadow hover:shadow-md">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-[#0D3B2B]">{fmtDate(b.date, lang)}</span>
          <span className="text-sm text-[#4A6358]">{b.slot_label}</span>
        </div>
        <p className="mt-0.5 line-clamp-1 text-sm text-[#4A6358]">{b.scrap_types.map(t).join(", ")}</p>
        <p className="mt-1 text-xs text-[#7B968B]">{b.booking_id}{b.status === "Completed" ? ` · ${b.total_kg} kg` : ""}</p>
      </div>
      <StatusBadge status={b.status} />
      <ChevronRight className="h-5 w-5 text-[#7B968B]" />
    </Link>
  );
};

const fyStart = () => { const d = new Date(); return d.getMonth() >= 3 ? d.getFullYear() - 1 : d.getFullYear() - 2; };

const FyStatement = () => {
  const { t } = useApp();
  const [y, setY] = useState(fyStart());
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-2xl border-2 border-[#2E9E6B] bg-white p-4" data-testid="fy-statement-box">
      <p className="w-full font-display font-bold text-[#0D3B2B]">{t("Financial year statement")}</p>
      <p className="w-full text-xs text-[#4A6358]">{t("All pickups from 1 April to 31 March in one PDF. Keep it as your record of dry waste sent for recycling (useful for the annual return due by 30 June).")}</p>
      <select value={y} onChange={(e) => setY(Number(e.target.value))} className="h-12 flex-1 rounded-xl border border-[#D2E0D8] bg-white px-3 text-base" data-testid="fy-select">
        {[0, 1, 2].map((k) => fyStart() + 1 - k).filter((v) => v >= 2024).map((v) => <option key={v} value={v}>FY {v}-{String(v + 1).slice(2)}</option>)}
      </select>
      <a href={`${API}/statement/fy?year=${y}`} data-testid="fy-download-btn"
        className="flex h-12 items-center gap-2 rounded-xl bg-[#2E9E6B] px-4 font-semibold text-white"><FileDown className="h-5 w-5" />{t("Download PDF")}</a>
    </div>
  );
};

const Statement = () => {
  const { t } = useApp();
  const [month, setMonth] = useState(todayIST().slice(0, 7));
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-2xl border border-[#D2E0D8] bg-white p-4" data-testid="statement-box">
      <p className="w-full font-display font-bold text-[#0D3B2B]">{t("Monthly statement")}</p>
      <p className="w-full text-xs text-[#4A6358]">{t("One PDF with all completed pickups, weights and payments for the month.")}</p>
      <input type="month" value={month} max={todayIST().slice(0, 7)} onChange={(e) => setMonth(e.target.value)}
        className="h-12 flex-1 rounded-xl border border-[#D2E0D8] px-3 text-base" data-testid="statement-month-input" />
      <a href={`${API}/statement?month=${month}`} data-testid="statement-download-btn"
        className="flex h-12 items-center gap-2 rounded-xl bg-[#0D3B2B] px-4 font-semibold text-white"><FileDown className="h-5 w-5" />{t("Download PDF")}</a>
    </div>
  );
};

export default function History() {
  const { t, user } = useApp();
  const isBiz = !!user?.customer_type && user.customer_type !== "Household";
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/bookings").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  useSync(() => api.get("/bookings").then((r) => setRows(r.data)).catch(() => {}));
  if (!rows) return <p className="text-[#7B968B]">{t("Loading…")}</p>;
  const today = todayIST();
  const upcoming = rows.filter((b) => ["Requested", "Confirmed"].includes(b.status) && b.date >= today).reverse();
  const past = rows.filter((b) => !upcoming.includes(b));
  return (
    <div className="space-y-6">
      <PageTitle sub={t("All your pickups in one place. Tap one to see details.")}>{t("History")}</PageTitle>
      {rows.some((b) => b.status === "Completed") && <Statement />}
      {(isBiz || new URLSearchParams(window.location.search).get("fy")) && <FyStatement />}
      {!rows.length && (
        <div className="space-y-4 py-10 text-center" data-testid="history-empty">
          <Inbox className="mx-auto h-12 w-12 text-[#7B968B]" />
          <p className="text-[#4A6358]">{t("You haven't booked any pickups yet.")}</p>
          <BigButton coral onClick={() => nav("/book")} data-testid="history-book-btn">{t("Book a pickup")}</BigButton>
        </div>
      )}
      {upcoming.length > 0 && (
        <section className="space-y-3" data-testid="history-upcoming">
          <h2 className="text-xs font-bold uppercase tracking-widest text-[#2E9E6B]">{t("Upcoming")}</h2>
          {upcoming.map((b) => <Row key={b.booking_id} b={b} />)}
        </section>
      )}
      {past.length > 0 && (
        <section className="space-y-3" data-testid="history-past">
          <h2 className="text-xs font-bold uppercase tracking-widest text-[#7B968B]">{t("Past")}</h2>
          {past.map((b) => <Row key={b.booking_id} b={b} />)}
        </section>
      )}
    </div>
  );
}
